import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";

import UnifiedSportsbookHistory from "../features/sportsbookHistory/display/unifiedSportsbookHistory.display";
import CasinoBettingHistoryDisplay from "../features/sportsbookHistory/display/casinoBettingHistory.display";
import {
  formatSportsbookHistoryMoney,
  formatSportsbookHistoryOdds,
} from "../features/sportsbookHistory/display/sportsbookHistoryDecimal.display";
import { getSportsbookHistorySelectionStatusDisplay } from "../features/sportsbookHistory/display/sportsbookHistorySelectionStatus.display";
import { canHideSportsbookHistoryRow } from "../features/sportsbookHistory/display/sportsbookHistoryHiddenRows.display";
import { getMarketTitle } from "./Sports/figma/marketTitleResolver";
import { getSelectionLabel as resolveSportsSelectionLabel } from "./Sports/figma/selectionLabelResolver";
import "./sportsbook-history.css";

const SPORTSBOOK_TAB = "Sportsbook";
const HISTORY_TABS = Object.freeze([
  { key: SPORTSBOOK_TAB, label: "스포츠북" },
  { key: "Entire", label: "전체" },
  { key: "Slots", label: "슬롯" },
  { key: "Live Casino", label: "라이브 카지노" },
  { key: "Sports", label: "외부 스포츠" },
  { key: "Minigames", label: "미니게임" },
]);
const HISTORY_TAB_KEYS = new Set(HISTORY_TABS.map(({ key }) => key));
const SHARED_DATE_OPTIONS = Object.freeze([
  { value: "today", label: "오늘" },
  { value: "7days", label: "7일" },
  { value: "30days", label: "30일" },
  { value: "custom", label: "직접입력" },
]);
const SPORTSBOOK_DATE_OPTIONS = Object.freeze([
  { value: "all", label: "전체기간" },
  ...SHARED_DATE_OPTIONS,
]);

const BET_STATUS_META = Object.freeze({
  PENDING: { key: "pending", label: "처리중", color: "#a8b2ba", background: "rgba(168,178,186,.14)" },
  CONFIRMED: { key: "confirmed", label: "확정", color: "#f2b84b", background: "rgba(242,184,75,.15)" },
  WON_PENDING: { key: "confirmed", label: "당첨 대기", color: "#f2b84b", background: "rgba(242,184,75,.15)" },
  WON: { key: "won", label: "당첨", color: "#59f477", background: "rgba(89,244,119,.14)" },
  HALF_WIN: { key: "half-won", label: "반적중", color: "#65c7ff", background: "rgba(101,199,255,.14)" },
  PARTIAL_WON: { key: "half-won", label: "부분당첨", color: "#65c7ff", background: "rgba(101,199,255,.14)" },
  LOST: { key: "lost", label: "낙첨", color: "#ff6b7a", background: "rgba(255,107,122,.14)" },
  HALF_LOSE: { key: "half-lost", label: "반미적중", color: "#ffad5c", background: "rgba(255,173,92,.14)" },
  PARTIAL_LOST: { key: "half-lost", label: "부분미적중", color: "#ffad5c", background: "rgba(255,173,92,.14)" },
  PARTIAL: { key: "void", label: "부분정산", color: "#c8b5ff", background: "rgba(200,181,255,.14)" },
  VOID: { key: "void", label: "무효", color: "#c8b5ff", background: "rgba(200,181,255,.14)" },
  CANCELLED: { key: "cancelled", label: "취소", color: "#a8b2ba", background: "rgba(168,178,186,.14)" },
  REJECTED: { key: "lost", label: "거절", color: "#ff6b7a", background: "rgba(255,107,122,.14)" },
  FAILED: { key: "lost", label: "실패", color: "#ff6b7a", background: "rgba(255,107,122,.14)" },
});

const normalizeStatus = (value) => {
  const status = String(value || "").trim().toUpperCase();
  if (status === "HALF_LOSS") return "HALF_LOSE";
  if (status === "CANCELED") return "CANCELLED";
  return status;
};

const getBetStatusMeta = (bet) => {
  const status = normalizeStatus(bet.display_status || bet.status);
  const meta = BET_STATUS_META[status] || BET_STATUS_META.PENDING;
  return { ...meta, label: bet.status_label || meta.label };
};

const formatScore = (selection) => {
  const home = selection?.match?.final_score_home ?? selection?.match?.live_score_home;
  const away = selection?.match?.final_score_away ?? selection?.match?.live_score_away;
  return home == null || away == null ? "-" : `${home} : ${away}`;
};

const formatFixtureTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "";
  return new Intl.DateTimeFormat("ko-KR", {
    timeZone: "Asia/Seoul",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const UNDER_OVER_PATTERN = /(under\/over|over\/under|asian under\/over|오버|언더)/i;
const hasLineSuffix = (label) => /\([^()]+\)/.test(String(label || ""));

const getHistoryMarketLabel = (selection) => {
  const market =
    selection.effective_market_name_ko ||
    selection.market_name_ko ||
    selection.effective_market_name ||
    selection.market_name ||
    "마켓";
  const resolved = getMarketTitle(market) || market;
  const line = String(selection.line_snapshot ?? "").trim();
  return line && !hasLineSuffix(resolved) ? `${resolved} (${line})` : resolved;
};

const getHistorySelectionLabel = (selection) => {
  const market = getHistoryMarketLabel(selection);
  const rawLabel =
    selection.effective_selection_display_name ||
    selection.selection_display_name_snapshot ||
    resolveSportsSelectionLabel(
      { name: selection.selection_name },
      { marketName: market, match: selection.match }
    ) ||
    selection.selection_name ||
    "선택지";
  const line = String(selection.line_snapshot ?? "").trim();
  return line && UNDER_OVER_PATTERN.test(market) && !hasLineSuffix(rawLabel)
    ? `${rawLabel} (${line})`
    : rawLabel;
};

const isSelectedSnapshotItem = (item, selection) =>
  [item?.key, item?.label].some(
    (value) => String(value || "").trim() === String(selection.selection_name || "").trim()
  );

const HistoryCard = ({ bet, detailState, canceling, onExpand, onCancel, onHide }) => {
  const [expanded, setExpanded] = useState(false);
  const selections = bet.selections || [];
  const firstSelection = selections[0];
  const selectionCount = Number.isInteger(bet.selection_count) ? bet.selection_count : selections.length;
  const remainingCount = Math.max(0, selectionCount - 1);
  const statusMeta = getBetStatusMeta(bet);
  const canHide = canHideSportsbookHistoryRow(bet);
  const betPlacedAt = formatFixtureTime(bet.created_at);

  const handleToggle = () => {
    setExpanded((current) => {
      const next = !current;
      if (next && detailState?.status === "idle") onExpand();
      return next;
    });
  };

  return (
    <article className={`crypto-history-card is-${statusMeta.key}${expanded ? " is-expanded" : ""}`}>
      <button
        type="button"
        className="crypto-history-summary"
        onClick={handleToggle}
        aria-expanded={expanded}
      >
        <span className="crypto-history-identity">
          <span>
            <strong>{bet.display_betting_category_label || "스포츠"}</strong>
            <small>{bet.bet_type === "SINGLE" ? "단폴" : `${selectionCount}폴 조합`}</small>
          </span>
          <small className="crypto-history-bet-reference">
            <span>{bet.bet_uid || bet.history_id}</span>
            {betPlacedAt ? <time dateTime={bet.created_at}>베팅 {betPlacedAt}</time> : null}
          </small>
        </span>
        <span
          className="crypto-history-status"
          style={{ "--history-status-color": statusMeta.color, "--history-status-bg": statusMeta.background }}
        >
          {statusMeta.label}
          {bet.archive_read_only ? <small>보관 기록 · 읽기 전용</small> : null}
        </span>
        <span><small>베팅금</small>{formatSportsbookHistoryMoney(bet.stake)}원</span>
        <span className="crypto-history-total-odds">
          <small>총 배당</small>
          {formatSportsbookHistoryOdds(bet.display_total_odds ?? bet.total_odds)}
        </span>
        <span className={statusMeta.key === "won" ? "is-won" : ""}>
          <small>{bet.settled_at ? "정산금" : "예상 당첨"}</small>
          {formatSportsbookHistoryMoney(bet.settled_at ? bet.display_actual_win : bet.display_potential_win)}원
        </span>
        <span className="crypto-history-expand-icon" aria-hidden="true">{expanded ? "−" : "+"}</span>
      </button>

      <div className="crypto-history-pick-preview">
        {firstSelection ? (
          <>
            <span>
              <strong>{firstSelection.match_name}</strong>
              <small>{formatFixtureTime(firstSelection.match?.start_time)}</small>
            </span>
            <span>
              <small>{getHistoryMarketLabel(firstSelection)}</small>
              <strong>{getHistorySelectionLabel(firstSelection)}</strong>
            </span>
            <em>@ {formatSportsbookHistoryOdds(firstSelection.effective_odds_snapshot ?? firstSelection.odds_snapshot)}</em>
            {remainingCount > 0 ? <b>외 {remainingCount}건</b> : null}
          </>
        ) : (
          <span><strong>{selectionCount}개 선택</strong><small>상세를 열어 선택지를 확인하세요.</small></span>
        )}
      </div>

      {expanded && detailState?.status === "loading" ? <p className="crypto-history-state">상세 불러오는 중…</p> : null}
      {expanded && detailState?.status === "error" ? <p className="crypto-history-state is-error">상세 내역을 불러오지 못했습니다.</p> : null}
      {expanded && selections.length > 0 ? (
        <div className="crypto-history-selections">
          {selections.map((selection) => {
            const resultMeta = getSportsbookHistorySelectionStatusDisplay(selection.result);
            const snapshotItems =
              selection.effective_market_display_snapshot?.items || selection.market_display_snapshot?.items;
            return (
              <div className="crypto-history-selection" key={selection.selection_ref || selection.id}>
                <div className="crypto-history-selection-head">
                  <span>
                    <strong>{selection.match_name}</strong>
                    <small>{formatFixtureTime(selection.match?.start_time)}</small>
                  </span>
                  <span
                    className="crypto-history-outcome"
                    style={{ "--history-status-color": resultMeta.color, "--history-status-bg": resultMeta.background }}
                  >
                    {resultMeta.label}
                  </span>
                </div>
                <div className="crypto-history-selection-main">
                  <span>
                    <small>{getHistoryMarketLabel(selection)}</small>
                    <strong>{getHistorySelectionLabel(selection)}</strong>
                  </span>
                  <em>@ {formatSportsbookHistoryOdds(selection.effective_odds_snapshot ?? selection.odds_snapshot)}</em>
                </div>
                <div className="crypto-history-selection-meta">
                  <span>베팅 시 스코어 <strong>{selection.score_at_bet || "-"}</strong></span>
                  <span>최종 스코어 <strong>{formatScore(selection)}</strong></span>
                </div>
                {snapshotItems?.length ? (
                  <div className="crypto-history-market-snapshot">
                    {snapshotItems.map((item) => (
                      <span
                        className={isSelectedSnapshotItem(item, selection) ? "is-selected" : ""}
                        key={`${item.id || item.key || item.label || "market"}-${item.odds ?? "no-odds"}`}
                      >
                        {item.label || item.key}
                        {item.odds != null ? <strong>{formatSportsbookHistoryOdds(String(item.odds))}</strong> : null}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}

      {bet.capabilities?.can_cancel || canHide ? <div className="crypto-history-actions">
        {bet.capabilities?.can_cancel ? (
          <button type="button" disabled={canceling} onClick={onCancel}>
            {canceling ? "취소 중" : "베팅 취소"}
          </button>
        ) : null}
        {canHide ? <button type="button" onClick={onHide}>목록에서 숨기기</button> : null}
      </div> : null}
    </article>
  );
};

const CursorPagination = ({ currentPage, canGoBack, canGoNext, loading, onBack, onNext }) => (
  <div className="crypto-history-pagination">
    <button type="button" disabled={!canGoBack || loading} onClick={onBack}>이전</button>
    <span>{currentPage}</span>
    <button type="button" disabled={!canGoNext || loading} onClick={onNext}>다음</button>
  </div>
);

const SportsbookHistoryPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const activeTab = HISTORY_TAB_KEYS.has(requestedTab) ? requestedTab : SPORTSBOOK_TAB;
  const [dateRange, setDateRange] = useState(() => activeTab === SPORTSBOOK_TAB ? "all" : "today");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const isSportsbookTab = activeTab === SPORTSBOOK_TAB;
  const effectiveDateRange = !isSportsbookTab && dateRange === "all" ? "today" : dateRange;
  const dateOptions = isSportsbookTab ? SPORTSBOOK_DATE_OPTIONS : SHARED_DATE_OPTIONS;

  const handleTabChange = (tab) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("tab", tab);
    setSearchParams(nextParams, { replace: true });
    setDateRange(tab === SPORTSBOOK_TAB ? "all" : "today");
    setCustomStartDate("");
    setCustomEndDate("");
  };

  return (
    <section className="sportsbook-theme crypto-history-page">
      <header>
        <div>
          <span>BETTING HISTORY</span>
          <h1>베팅내역</h1>
        </div>
      </header>

      <nav className="crypto-history-tabs" role="tablist" aria-label="베팅내역 종류">
        {HISTORY_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.key}
            className={activeTab === tab.key ? "active" : ""}
            onClick={() => handleTabChange(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <div className="crypto-history-date-filters" aria-label="조회 기간">
        {dateOptions.map((option) => (
          <button
            key={option.value}
            type="button"
            className={effectiveDateRange === option.value ? "active" : ""}
            onClick={() => setDateRange(option.value)}
          >
            {option.label}
          </button>
        ))}
        {effectiveDateRange === "custom" ? (
          <span className="crypto-history-custom-range">
            <input
              type="date"
              aria-label="조회 시작일"
              value={customStartDate}
              onChange={(event) => setCustomStartDate(event.target.value)}
            />
            <i>~</i>
            <input
              type="date"
              aria-label="조회 종료일"
              value={customEndDate}
              onChange={(event) => setCustomEndDate(event.target.value)}
            />
          </span>
        ) : null}
      </div>

      <div className="crypto-history-tab-panel" role="tabpanel">
        {isSportsbookTab ? (
          <UnifiedSportsbookHistory
            dateRange={effectiveDateRange}
            customStartDate={customStartDate}
            customEndDate={customEndDate}
            CardComponent={HistoryCard}
            CursorPaginationComponent={CursorPagination}
          />
        ) : (
          <CasinoBettingHistoryDisplay
            key={`${activeTab}:${effectiveDateRange}:${customStartDate}:${customEndDate}`}
            tab={activeTab}
            dateRange={effectiveDateRange}
            customStartDate={customStartDate}
            customEndDate={customEndDate}
          />
        )}
      </div>
    </section>
  );
};

export default SportsbookHistoryPage;
